// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { getConfluencePageAriFromContentRef } from '@databases/connections/utils/ariHelper';
import { getReferencedPageField } from '@databases/content-api/database/field-types/pageLabels';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
} from '@databases/content-api/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/content-api/redux/databases/selectors';
import { type AsyncOperation } from '@databases/redux-types';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseWithoutEntries,
} from '@databases/types/database';

import { type DefaultRootState } from '../redux';

import {
  type Connection,
  type ConnectionColumns,
  type ConnectionsState,
  type ContentItem,
  type ContentItems,
} from './slice';

export type ConnectionContentWithColumns = {
  ari: string;
  data: ContentItems;
  columns: ConnectionColumns;
};

export const selectContentOptional = (
  state: ConnectionsState,
  ari: string
): ConnectionContentWithColumns | undefined => {
  const content = state.content[ari];
  if (!content) {
    return undefined;
  }

  const connection = state.connections[content.connectionId];
  if (!connection) {
    return undefined;
  }

  return {
    ari: content.ari,
    data: content.item,
    columns: connection.columns,
  };
};

export const selectContentPropertyOptional = (
  state: ConnectionsState,
  ari: string,
  property: string
): ContentItem | undefined => {
  const content = state.content[ari];
  if (!content) {
    return undefined;
  }

  return content['item'][property];
};

export const selectConnectionOptional = (
  state: ConnectionsState,
  connectionId: string
): Connection | undefined => {
  const connection = state.connections[connectionId];
  if (!connection) {
    return undefined;
  }

  return connection;
};

export function selectContentStatus(
  state: ConnectionsState,
  ari: string
): AsyncOperation<void> | undefined {
  if (!state.status[ari]) {
    return undefined;
  }

  return state.status[ari].content;
}

export function selectContentColumnStatus(
  state: ConnectionsState,
  ari: string,
  column: string
): AsyncOperation<void> | undefined {
  if (!state.status[ari]) {
    return undefined;
  }

  return state.status[ari].columns?.[column];
}

export const getContentForReference = (
  database: DatabaseWithoutEntries<ObjectMode, Database<ObjectMode>>,
  field: DatabaseField,
  entry: DatabaseEntry | undefined,
  state: DefaultRootState
): ConnectionContentWithColumns[] => {
  if (!entry) {
    return [];
  }

  const referencedField = getReferencedPageField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);

  const resolvedRefs: ConnectionContentWithColumns[] = [];
  if (referencedValue?.value) {
    for (const contentRef of referencedValue.value) {
      const ari = getConfluencePageAriFromContentRef(contentRef);
      if (ari) {
        const resolvedRef = selectContentOptional(state.connections, ari);
        if (resolvedRef) {
          resolvedRefs.push(resolvedRef);
        }
      }
    }
  }

  return resolvedRefs;
};

export const getReferencedContentById = (
  contentId: string,
  field: DatabaseField,
  entryId: string,
  state: DefaultRootState
): ConnectionContentWithColumns[] => {
  const database = selectDatabaseByContentIdRequired(state, {
    contentId,
  });
  const entry = getDatabaseEntryRequired(database, entryId);

  return getContentForReference(database, field, entry, state);
};

export const getReferencedContentStatusById = (
  contentId: string,
  field: DatabaseField,
  entryId: string,
  column: string,
  state: DefaultRootState
): (AsyncOperation<void> | undefined)[] => {
  const database = selectDatabaseByContentIdRequired(state, {
    contentId,
  });
  const entry = getDatabaseEntryRequired(database, entryId);

  if (!entry) {
    return [];
  }

  const referencedField = getReferencedPageField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);

  const resolvedRefs: (AsyncOperation<void> | undefined)[] = [];
  if (referencedValue?.value) {
    for (const contentRef of referencedValue.value) {
      const ari = getConfluencePageAriFromContentRef(contentRef);
      if (ari) {
        const connection = selectConnectionOptional(
          state.connections,
          referencedField?.id || ''
        );
        let resolvedStatus = selectContentColumnStatus(state.connections, ari, column);
        if (!resolvedStatus && connection?.requiredColumns.includes(column)) {
          resolvedStatus = selectContentStatus(state.connections, ari);
        }
        resolvedRefs.push(resolvedStatus);
      }
    }
  }

  return resolvedRefs;
};
